import React, {useEffect, useState} from "react";
import {Button, Card, Checkbox, Col, Form, Input, Row, Space, Tooltip} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {observer} from "mobx-react";
import {AgentNewContext} from "../../agent.new.modal";
import {useTranslate} from "../../../../../../hooks/translate.hook";
import {SystemAccesses} from "../../../../../users/users/system-accesses/system.accesses";
import {setGeneratedPassword} from "../../../agent-data-accounts/agent.data.accounts.modal";
import {useAgentNewStepCardStyles} from "../agent.new.step.card.styles";

export const AccountStep: React.FC = observer(() => {
    const {setStep, setValue, getValue, onComplete, fields} = React.useContext(AgentNewContext);
    const t = useTranslate();
    const [loading, setLoading] = useState<boolean>(false);
    const [form] = Form.useForm();

    useEffect(() => {
        form.setFields(fields);
    }, [fields, form]);

    let initialValues: any = {};
    const value = getValue("user");
    if (value) {
        initialValues = {
            systemAccesses: value.systemAccesses,
            user: {user: value.user},
        };
    } else {
        const addressValues = getValue("billingAddress");
        if (addressValues) {
            initialValues = {
                systemAccesses: [{system: "fms_ws"}],
                user: {
                    user: {
                        firstName: addressValues.firstName,
                        lastName: addressValues.lastName,
                        email: addressValues.email,
                    },
                },
            };
        }
    }
    const cardStyles = useAgentNewStepCardStyles();

    return (
        <Form
            size="middle"
            form={form}
            layout="vertical"
            initialValues={initialValues}
            onFinish={async (values) => {
                await form.validateFields();
                setValue("user", {
                    systemAccesses: values.systemAccesses,
                    user: values.user.user,
                });

                setLoading(true);
                try {
                    await onComplete();
                } finally {
                    setLoading(false);
                }
            }}
        >
            <Row gutter={[16, 16]} align="top">
                <Col xs={24} md={12}>
                    <Card
                        size="small"
                        title={t("USER.PROFILE.DETAILS", {defaultValue: "User details"})}
                        style={{width: "100%"}}
                        styles={cardStyles}
                    >
                        <Form.Item
                            name={["user", "user", "firstName"]}
                            label={t("FIRSTNAME")}
                            rules={[{required: true, message: t("FROM.ERROR.PLEASE_ENTER_VALUE")}]}
                        >
                            <Input disabled={loading} type="text" placeholder={t("FIRSTNAME")} />
                        </Form.Item>
                        <Form.Item
                            name={["user", "user", "lastName"]}
                            label={t("LASTNAME")}
                            rules={[{required: true, message: t("FROM.ERROR.PLEASE_ENTER_VALUE")}]}
                        >
                            <Input disabled={loading} type="text" placeholder={t("LASTNAME")} />
                        </Form.Item>
                        <Form.Item
                            name={["user", "user", "email"]}
                            label={t("EMAIL")}
                            rules={[{required: true, message: t("FROM.ERROR.PLEASE_ENTER_VALUE")}]}
                        >
                            <Input disabled={loading} type="text" placeholder={t("EMAIL")} />
                        </Form.Item>
                        <Form.Item
                            name={["user", "user", "password"]}
                            label={t("PASSWORD.TITLE")}
                        >
                            <Input.Password
                                disabled={loading}
                                placeholder={t("PASSWORD.EDIT")}
                                allowClear
                                addonAfter={
                                    <Tooltip title={t("PASSWORD.GENERATE")}>
                                        <FontAwesomeIcon
                                            icon={["fas", "dice"]}
                                            style={{cursor: "pointer"}}
                                            onClick={() => setGeneratedPassword(
                                                form,
                                                ["user", "user", "password"]
                                            )}
                                        />
                                    </Tooltip>
                                }
                                iconRender={(visible) => visible ? (
                                    <Tooltip title={t("PASSWORD.HIDE")}>
                                        <FontAwesomeIcon style={{marginLeft: 3}} icon={["fas", "eye-slash"]} />
                                    </Tooltip>
                                ) : (
                                    <Tooltip title={t("PASSWORD.SHOW")}>
                                        <FontAwesomeIcon style={{marginLeft: 3}} icon={["fas", "eye"]} />
                                    </Tooltip>
                                )}
                            />
                        </Form.Item>
                        <Form.Item
                            noStyle
                            shouldUpdate={(previousValues, currentValues) =>
                                previousValues.user?.user?.password !== currentValues.user?.user?.password
                            }
                        >
                            {({getFieldValue}) => getFieldValue(["user", "user", "password"]) ? (
                                <Form.Item
                                    name={["user", "user", "sendPassword"]}
                                    valuePropName="checked"
                                >
                                    <Checkbox disabled={loading}>
                                        {t("PASSWORD.SEND_PASSWORD")}
                                    </Checkbox>
                                </Form.Item>
                            ) : null}
                        </Form.Item>
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card
                        size="small"
                        title={t("USER.PROFILE.SYSTEM_ACCESSES")}
                        style={{width: "100%"}}
                        styles={cardStyles}
                    >
                        <SystemAccesses loading={loading} type="wholesale" />
                    </Card>
                </Col>
            </Row>

            <Row justify="end" style={{marginTop: 16}}>
                <Col>
                    <Space size={10}>
                        <Button disabled={loading} onClick={() => setStep(4)}>
                            {t("BACK", {defaultValue: "Back"})}
                        </Button>
                        <Button disabled={loading} type="primary" onClick={() => form.submit()}>
                            {t("COMPLETE", {defaultValue: "Complete"})}
                        </Button>
                    </Space>
                </Col>
            </Row>
        </Form>
    );
});
